import axios from '@/axios';

// How long a location stays usable before we try to confirm it again. An expired entry is
// re-checked, never silently replaced by a weaker source (see SOURCE_RANK).
const LOCATION_TTLS = {
  'approximate-ip': 24 * 60 * 60 * 1000,
  saved: 24 * 60 * 60 * 1000,
  device: 30 * 24 * 60 * 60 * 1000,
  manual: 365 * 24 * 60 * 60 * 1000,
};

// An explicit pick beats a GPS fix, which beats an IP guess. `saved` is a legacy entry with no
// recorded source, so it is treated as weak.
const SOURCE_RANK = {
  saved: 0,
  'approximate-ip': 1,
  device: 2,
  manual: 3,
};

const IP_RANK = SOURCE_RANK['approximate-ip'];

// An IP guess can go stale the moment the user switches network (wifi ↔ cellular, VPN, relay),
// so weak entries are revalidated in the background well before their TTL runs out.
const REVALIDATE_AFTER = 30 * 60 * 1000;

const STORAGE_KEYS = [
  'cityCode',
  'cityName',
  'countryCode',
  'latitude',
  'longitude',
  'nearbyCities',
  'locationSource',
  'locationDetectedAt',
  'locationConfidence',
  'locationNetworkKey',
  'locationSignals',
  'lastLocationUpdateTime',
];

let approximateLocationRequest = null;
let initialLocationRequest = null;
let revalidationRequest = null;

const rankOf = (source) => SOURCE_RANK[source] ?? 0;

const readStoredSignals = () => {
  try {
    return JSON.parse(localStorage.getItem('locationSignals') || 'null');
  } catch (_) {
    return null;
  }
};

const readStoredLocation = () => {
  const code = localStorage.getItem('cityCode');
  const name = localStorage.getItem('cityName');
  if (!code || !name) return null;

  return {
    code,
    name,
    countryCode: localStorage.getItem('countryCode') || '',
    source: localStorage.getItem('locationSource') || 'saved',
    detectedAt: Number(localStorage.getItem('locationDetectedAt') || 0),
    confidence: localStorage.getItem('locationConfidence') || '',
    networkKey: localStorage.getItem('locationNetworkKey') || '',
    signals: readStoredSignals(),
  };
};

const storedLocationResponse = (stored) => ({
  detected: true,
  city: {
    name: stored.name,
    code: stored.code,
    country_code: stored.countryCode,
  },
  accuracy: stored.source,
  confidence: stored.confidence || undefined,
  // Kept so a prefill made from the cache still reports which database proposed
  // the city and how corroborated it was.
  signals: stored.signals || undefined,
});

const isFresh = (stored) => {
  const ttl = LOCATION_TTLS[stored.source] || 0;
  return stored.detectedAt > 0 && Date.now() - stored.detectedAt < ttl;
};

const isStale = (stored) => Date.now() - stored.detectedAt > REVALIDATE_AFTER;

export const clearStoredLocation = () => {
  initialLocationRequest = null;
  STORAGE_KEYS.forEach((key) => localStorage.removeItem(key));
};

export const persistLocation = (location, source = 'approximate-ip') => {
  if (!location?.city?.code || !(location.city.display_name || location.city.name)) return;

  const resolved = {
    ...location,
    detected: true,
    accuracy: source,
  };
  localStorage.setItem('cityCode', location.city.code);
  localStorage.setItem('cityName', location.city.display_name || location.city.name);
  localStorage.setItem('countryCode', location.city.country_code || '');
  localStorage.setItem('locationSource', source);
  localStorage.setItem('locationDetectedAt', Date.now().toString());
  if (location.confidence) {
    localStorage.setItem('locationConfidence', location.confidence);
  } else {
    localStorage.removeItem('locationConfidence');
  }
  if (location.signals?.network_key) {
    localStorage.setItem('locationNetworkKey', location.signals.network_key);
  }
  if (location.signals) {
    localStorage.setItem('locationSignals', JSON.stringify(location.signals));
  } else {
    localStorage.removeItem('locationSignals');
  }
  initialLocationRequest = Promise.resolve(resolved);

  if (source === 'approximate-ip') {
    localStorage.removeItem('latitude');
    localStorage.removeItem('longitude');
    localStorage.removeItem('nearbyCities');
  }
};

export const getApproximateLocation = () => {
  if (!approximateLocationRequest) {
    approximateLocationRequest = axios
      .get('/search/approximate-location/', { timeout: 5000 })
      .then((response) => response.data)
      .catch(() => ({ detected: false }))
      .then((location) => {
        if (!location?.detected) {
          setTimeout(() => {
            approximateLocationRequest = null;
            initialLocationRequest = null;
          }, 5 * 60 * 1000);
        }
        return location;
      });
  }

  return approximateLocationRequest;
};

// `null` means the check itself failed (offline, backend down) and must not be read as
// confirmation; `{ valid: false }` means the city is genuinely gone.
const validateStoredLocation = (stored) => axios
  .get('/search/location-city/', {
    params: { code: stored.code },
    timeout: 3000,
  })
  .then((response) => response.data)
  .catch(() => null);

// A location is only good enough to fill a search field when the backend says so. Backends that
// predate the `confidence` field send nothing, and their answers keep the old behaviour.
export const isConfidentLocation = (location) => {
  if (!location?.detected || !location.city) return false;
  if (rankOf(location.accuracy) > IP_RANK) return true;
  return !location.confidence || location.confidence === 'high';
};

// Single decision point for "should we put this city into a departure field?", shared by every
// form so the same guess can never be applied twice with two different sets of rules.
export const getAutofillOrigin = async () => {
  const location = await resolveInitialLocation();
  return isConfidentLocation(location) ? location : null;
};

const revalidateInBackground = (stored) => {
  if (revalidationRequest) return;

  revalidationRequest = getApproximateLocation()
    .then((approximate) => {
      const networkKey = approximate?.signals?.network_key;
      const networkChanged = Boolean(networkKey) && Boolean(stored.networkKey)
        && networkKey !== stored.networkKey;
      if (!approximate?.detected || !approximate.city) return;
      if (!networkChanged && approximate.city.code === stored.code) return;

      persistLocation(approximate, 'approximate-ip');
      window.dispatchEvent(new CustomEvent('location-resolved', {
        detail: { city: approximate.city, source: 'approximate-ip' },
      }));
    })
    .catch(() => {})
    .finally(() => {
      revalidationRequest = null;
    });
};

export const resolveInitialLocation = () => {
  if (!initialLocationRequest) {
    initialLocationRequest = (async () => {
      const stored = readStoredLocation();

      if (stored && isFresh(stored)) {
        if (rankOf(stored.source) <= IP_RANK && isStale(stored)) revalidateInBackground(stored);
        return storedLocationResponse(stored);
      }

      const [approximate, validated] = await Promise.all([
        getApproximateLocation(),
        stored ? validateStoredLocation(stored) : Promise.resolve({ valid: false }),
      ]);

      // Expiry means "confirm this again", so a manual pick or a GPS fix survives an IP guess.
      // The timestamp is only refreshed on a real confirmation: a failed check (`null`) would
      // otherwise keep extending the TTL of a location nobody ever verified.
      if (stored && rankOf(stored.source) > IP_RANK && validated?.valid !== false) {
        if (validated?.valid) {
          persistLocation(validated, stored.source);
          return { ...validated, detected: true, accuracy: stored.source };
        }
        return storedLocationResponse(stored);
      }

      if (approximate?.detected && approximate.city) {
        persistLocation(approximate, 'approximate-ip');
        return approximate;
      }

      if (stored && validated?.valid) {
        persistLocation(validated, stored.source);
        return { ...validated, detected: true, accuracy: stored.source };
      }

      if (stored && validated === null) return storedLocationResponse(stored);

      if (stored) clearStoredLocation();
      const unresolved = { detected: false };
      initialLocationRequest = Promise.resolve(unresolved);
      return unresolved;
    })();
  }

  return initialLocationRequest;
};
